import { FunctionComponent, useMemo } from "react";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";

import {
  ButtonsGroupChoiceField,
  SubmissionForm,
  TextInputField,
  Grid,
  Heading,
  Separator,
  Stack,
  SubmissionFormButtons,
  mergeStyles
} from "@bps/fluent-ui";
import { isDefined } from "@bps/common-utils";
import { CopyableText } from "@components/CopyableText";
import { ExpBusinessRolesSelectField } from "@components/form/fields/ExpBusinessRolesSelectField";
import { ExpSecurityRolesSelectField } from "@components/form/fields/ExpSecurityRolesSelectField";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  PltUser,
  SecurityRolesAndPermissionsDto
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  PltCacheKeys,
  useUpdateUser
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { getUserDtoPayload } from "./user-details-form.utils";
import {
  UserDetailsFormProps,
  UserDetailsFormValues
} from "./UserDetailsForm.types";

export const UserDetailsForm: FunctionComponent<UserDetailsFormProps> = ({
  user,
  application
}) => {
  const { mutateAsync: updateUser } = useUpdateUser();

  const queryClient = useQueryClient();

  const { tenantId } = useParams<{ tenantId: string }>();

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltCatalogUserWrite
  );

  const disableAccess = user.tenantId !== tenantId || !hasWritePermission;

  const defaultValues: UserDetailsFormValues = useMemo(() => {
    return {
      firstName: user?.firstName,
      lastName: user?.lastName,
      isInactive:
        typeof user?.isInactive === "undefined" ? false : user.isInactive,
      status: user?.status,
      businessRoles: user.businessRoles,
      securityRoles: (user.securityRoles ?? [])
        .map(role => role.code)
        .filter(isDefined)
    };
  }, [user]);

  return (
    <SubmissionForm<UserDetailsFormValues>
      initialValues={defaultValues}
      onSubmit={async (values, form) => {
        const { securityRoles = [] } =
          queryClient.getQueryData<SecurityRolesAndPermissionsDto>([
            PltCacheKeys.SecurityRolesAndPermissions
          ]) ?? {};

        const userPayload: PltUser = getUserDtoPayload({
          user,
          securityRoles,
          values
        });

        await updateUser(userPayload);

        form.reset(values);
      }}
      disabled={disableAccess}
      hideButtons
    >
      {() => (
        <>
          <Separator />
          <Stack tokens={{ childrenGap: 8, maxWidth: 656 }}>
            {!disableAccess && (
              <SubmissionFormButtons
                classNames={{
                  buttonsWrapper: mergeStyles({ alignSelf: "end" })
                }}
                hideCancelButton
              />
            )}
            <Grid
              templateColumns="repeat(2, 320px)"
              childrenTokens={{ rowGap: 8, columnGap: 16 }}
            >
              <TextInputField name="firstName" label="First name" />
              <TextInputField name="lastName" label="Last name" />
              <ButtonsGroupChoiceField
                label="Status (Prem Site)"
                name="status"
                options={[
                  {
                    key: "ACTIVE",
                    text: "Enabled",
                    disabled: true
                  },
                  {
                    key: "PENDINGSIGNUP",
                    text: "Pending SignUp",
                    disabled: true
                  },
                  {
                    key: "INACTIVE",
                    text: "Disabled",
                    disabled: true
                  }
                ]}
              />
              <ButtonsGroupChoiceField
                label="Status"
                name="isInactive"
                options={[
                  {
                    key: false,
                    text: "Enabled",
                    disabled: disableAccess
                  },
                  {
                    key: true,
                    text: "Disabled",
                    disabled: disableAccess
                  }
                ]}
              />
              <ExpBusinessRolesSelectField
                name="businessRoles"
                application={application}
                label="Business roles"
              />
              <ExpSecurityRolesSelectField
                name="securityRoles"
                application={application}
                label="Security roles"
              />
              <Stack>
                <Heading labelPaddings>External Id</Heading>
                {user?.externalId ? (
                  <CopyableText clipboardContent={user?.externalId.toString()}>
                    {user?.externalId.toString()}
                  </CopyableText>
                ) : (
                  "-"
                )}
              </Stack>
            </Grid>
          </Stack>
        </>
      )}
    </SubmissionForm>
  );
};
